import { nanoid, type PayloadAction } from '@reduxjs/toolkit';
import {
  EQuestionType,
  type TQuestionPresentation,
  type TAvailableLanguageCodes,
  type TQuestionConversationSettings,
} from '@hubertai/screening-question-types';
import { DEFAULT_SCHEDULING_CONFIG } from 'types/questions/scheduling.types';
import { type QuestionEditorsState } from './Question.Editor.Slice';

interface QuestionGeneralState {
  _id?: string;
  id: string;
  version: number;
  type: EQuestionType;
  short_description: string;
  language: TAvailableLanguageCodes;
  availableLanguages: TAvailableLanguageCodes[];
  source_question_id?: string;
  conversation_settings?: TQuestionConversationSettings;
  presentation: TQuestionPresentation;
  currentLanguage: TAvailableLanguageCodes;

  stateModified: boolean;
}

const initialState: QuestionGeneralState = {
  _id: undefined,
  id: nanoid(),
  version: 4,
  type: EQuestionType.SingleSelect,
  short_description: '',
  language: 'en-gb',
  availableLanguages: [],
  source_question_id: undefined,
  presentation: { block: '', label: { 'en-gb': '' } },
  currentLanguage: 'en-gb',
  stateModified: false,
};

export const questionGeneralReducer = {
  resetGeneral: (state: QuestionEditorsState, action: PayloadAction<{ questionId: string }>) => {
    const { questionId } = action.payload;
    if (state.byId[questionId]) {
      state.byId[questionId] = {
        ...state.byId[questionId],
        ...initialState,
      };
    }
  },

  updateType: (state: QuestionEditorsState, action: PayloadAction<{ questionId: string; type: EQuestionType }>) => {
    const { questionId, type } = action.payload;
    if (!state.byId[questionId]) return;

    state.byId[questionId].type = type;

    // Scheduling (cal.com) questions start from a sensible default config.
    if (type === EQuestionType.Scheduling && !state.byId[questionId].scheduling) {
      state.byId[questionId].scheduling = { ...DEFAULT_SCHEDULING_CONFIG };
    }

    state.byId[questionId].stateModified = true;
  },

  updateShortDescription: (
    state: QuestionEditorsState,
    action: PayloadAction<{ questionId: string; description: string }>,
  ) => {
    const { questionId, description } = action.payload;
    if (!state.byId[questionId]) return;

    state.byId[questionId].short_description = description;
    state.byId[questionId].stateModified = true;
  },

  updateLanguage: (
    state: QuestionEditorsState,
    action: PayloadAction<{ questionId: string; language: TAvailableLanguageCodes }>,
  ) => {
    const { questionId, language } = action.payload;
    if (!state.byId[questionId]) return;

    state.byId[questionId].language = language;
    state.byId[questionId].stateModified = true;
  },

  updateConversationSettings: (
    state: QuestionEditorsState,
    action: PayloadAction<{ questionId: string; settings: TQuestionConversationSettings }>,
  ) => {
    const { questionId, settings } = action.payload;
    if (!state.byId[questionId]) return;

    const question = state.byId[questionId];
    const currentConversationSettings = { ...question.conversation_settings };
    question.conversation_settings = {
      ...(currentConversationSettings ?? {}),
      ...settings,
    };
    question.stateModified = true;
  },

  updateVoiceSettings: (
    state: QuestionEditorsState,
    action: PayloadAction<{ questionId: string; settings: { force_options_readout?: boolean } }>,
  ) => {
    const { questionId, settings } = action.payload;
    if (!state.byId[questionId]) return;

    const question = state.byId[questionId];
    // Cast until the published @hubertai/screening-question-types exposes `voice_settings`.
    const q = question as unknown as { voice_settings?: { force_options_readout?: boolean } };
    q.voice_settings = {
      ...(q.voice_settings ?? {}),
      ...settings,
    };
    question.stateModified = true;
  },

  updatePresentation: (
    state: QuestionEditorsState,
    action: PayloadAction<{ questionId: string; presentation: TQuestionPresentation }>,
  ) => {
    const { questionId, presentation } = action.payload;
    if (!state.byId[questionId]) return;

    state.byId[questionId].presentation = presentation;
    state.byId[questionId].stateModified = true;
  },

  markAsSaved: (state: QuestionEditorsState, action: PayloadAction<{ questionId: string }>) => {
    const { questionId } = action.payload;
    if (!state.byId[questionId]) return;

    state.byId[questionId].stateModified = false;
  },
};
